import { generate } from "@pdfme/generator";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { text, image } from "@pdfme/schemas";
import { pdfTemplatePromise as pdfTemplatePromiseDVInspectWithRepairs } from "@/pdf/dvInspectWithRepairs";
import { pdfTemplatePromise as pdfTemplatePromiseDVInspect } from "@/pdf/dvInspect";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function safeUUID(uuid: string | null | undefined) {
  if (!uuid) {
    return null;
  }
  return uuid;
}

export const fetchImageAsBase64 = async (
  imageUrl: string
): Promise<string | null> => {
  try {
    if (!imageUrl) {
      return null;
    }

    const normalizedUrl = imageUrl.replace(/\\/g, "/");

    if (
      normalizedUrl.startsWith("http://") ||
      normalizedUrl.startsWith("https://")
    ) {
      // Use the URL as is if it's already a full URL
      try {
        // Remove spaces from URL to prevent 404 errors
        const urlWithoutSpaces = normalizedUrl.replace(/\s+/g, "%20");

        const response = await fetch(urlWithoutSpaces, {
          credentials: "include",
          headers: {
            Accept: "image/*",
          },
        });

        if (!response.ok) {
          // console.warn(`Image not found: ${urlWithoutSpaces} (${response.status} ${response.statusText})`);
          // Return null if image not found, PDF will be generated without the image
          return null;
        }

        const arrayBuffer = await response.arrayBuffer();
        const base64 = Buffer.from(arrayBuffer).toString("base64");
        const contentType = response.headers.get("content-type");
        return `data:${contentType};base64,${base64}`;
      } catch (fetchError) {
        // console.warn(`Failed to fetch image: ${normalizedUrl}`, fetchError);
        // Return null on fetch error, PDF will be generated without the image
        return null;
      }
    } else {
      // For relative URLs, add the prefix
      const prefix =
        process.env.NODE_ENV === "production"
          ? "https://sparkstire.com/files/"
          : "http://localhost:3001/";

      // Adjust image URL and encode spaces
      const urlWithoutSpaces = normalizedUrl.replace(/\s+/g, "%20");
      const adjustedImageUrl = urlWithoutSpaces.startsWith("/")
        ? `${prefix}${urlWithoutSpaces.slice(1)}`
        : `${prefix}${urlWithoutSpaces}`;

      try {
        const response = await fetch(adjustedImageUrl, {
          credentials: "include",
          headers: {
            Accept: "image/*",
          },
        });

        if (!response.ok) {
          // console.warn(`Image not found: ${adjustedImageUrl} (${response.status} ${response.statusText})`);
          // Return null if image not found, PDF will be generated without the image
          return null;
        }

        const arrayBuffer = await response.arrayBuffer();
        const base64 = Buffer.from(arrayBuffer).toString("base64");
        const contentType = response.headers.get("content-type");
        return `data:${contentType};base64,${base64}`;
      } catch (fetchError) {
        // console.warn(`Failed to fetch image: ${adjustedImageUrl}`, fetchError);
        // Return null on fetch error, PDF will be generated without the image
        return null;
      }
    }
  } catch (error) {
    // console.error("Failed to process image:", imageUrl, error);
    // Return null on any error, PDF will be generated without the image
    return null;
  }
};

type Tire = {
  wheel_position: string;
  [key: string]: any; // Allow other properties
};

export const sortTiresCounterClockwise = (tires: Tire[]): Tire[] => {
  const sortedTires = tires.sort((a, b) => {
    // Regular expression to parse wheel position
    const regex = /^(\d+)([LRIO]*)$/;

    // Parse position components
    const parsePosition = (pos: string) => {
      const match = pos.match(regex);
      if (!match) return { num: Infinity, type: "" };

      const [, num, type] = match;
      return {
        num: parseInt(num, 10),
        type: type || "",
      };
    };

    const aPos = parsePosition(a.wheel_position);
    const bPos = parsePosition(b.wheel_position);

    // Define sorting priority for types
    const getTypePriority = (type: string) => {
      if (type.startsWith("L")) {
        return 1; // All lefts have higher priority
      }
      if (type.startsWith("R")) {
        return 2; // All rights have lower priority
      }
      return 3; // Default priority for any other type
    };

    // Sort by type priority first
    const typePriorityComparison =
      getTypePriority(aPos.type) - getTypePriority(bPos.type);
    if (typePriorityComparison !== 0) {
      return typePriorityComparison;
    }

    // Sort by axle number first
    if (aPos.num !== bPos.num) {
      // For lefts, sort by numeric position ascending
      if (aPos.type.startsWith("L")) {
        return aPos.num - bPos.num;
      }
      // For rights, sort by numeric position descending
      if (aPos.type.startsWith("R")) {
        return bPos.num - aPos.num;
      }
    }

    // For same axle number, sort by inner/outer
    // For left side, LO should be before LI
    if (aPos.type.startsWith("L")) {
      if (aPos.type.includes("O") && bPos.type.includes("I")) {
        return -1; // LO comes before LI
      }
      if (aPos.type.includes("I") && bPos.type.includes("O")) {
        return 1; // LI comes after LO
      }
    }

    // For right side, RI should be before RO
    if (aPos.type.startsWith("R")) {
      if (aPos.type.includes("I") && bPos.type.includes("O")) {
        return -1; // RI comes before RO
      }
      if (aPos.type.includes("O") && bPos.type.includes("I")) {
        return 1; // RO comes after RI
      }
    }

    return 0; // Default case if types are the same and not L or R
  });

  return sortedTires;
};

export const generatePDF = async (input: any) => {
  let sanitized: Record<string, string> = {};
  let failedItems: string[] = [];

  try {
    // Handle signature if present
    if (input.signature_path) {
      const base64Image = await fetchImageAsBase64(input.signature_path);
      if (base64Image) {
        sanitized.signature = base64Image;
      }
    }

    if (input.repair_signature) {
      const repair_signature = await fetchImageAsBase64(input.repair_signature);
      if (repair_signature) {
        sanitized.repair_signature = repair_signature;
      }
    }

    // Process vehicle inspection items
    const vehicleItems = [
      "air_compressor",
      "air_lines",
      "battery",
      "belts_and_hoses",
      "body",
      "brake_accessories",
      "brakes_parking",
      "brakes_service",
      "clutch",
      "coupling_devices",
      "defrosting_heater",
      "drive_line",
      "engine",
      "exhaust",
      "fifth_wheel",
      "fluid_levels",
      "frame_and_assembly",
      "front_axle",
      "fuel_tanks",
      "horn",
      "lights",
      "mirrors",
      "muffler",
      "oil_pressure",
      "radiator",
      "rear_end",
      "reflector",
      "safety_equipment",
      "starter",
      "steering",
      "suspension_system",
      "tire_chains",
      "tires",
      "transmission",
      "trip_recorder",
      "wheels_and_rims",
      "windows",
      "windshield_wipers",
    ];

    vehicleItems.forEach((item) => {
      if (input[item]) {
        if (input[item] === "true") {
          sanitized[item] = "~";
        } else if (input[item] === "false") {
          sanitized[item] = "X";
          failedItems.push(item.replace(/_/g, " ").toUpperCase());
        } else if (input[item] === "not_applicable") {
          sanitized[item] = "N/A";
        }

        // Handle safety equipment items
        if (item === "safety_equipment") {
          const safetyValue =
            input[item] === "true"
              ? "~"
              : input[item] === "false"
                ? "X"
                : "N/A";
          sanitized.safety_fire = safetyValue;
          sanitized.safety_flags = safetyValue;
          sanitized.safety_reflective = safetyValue;
          sanitized.safety_bulbs = safetyValue;
          sanitized.spare_seal_beam = safetyValue;
        }

        // Handle lights items
        if (item === "lights") {
          const lightsValue =
            input[item] === "true"
              ? "~"
              : input[item] === "false"
                ? "X"
                : "N/A";
          sanitized.lights_head = lightsValue;
          sanitized.lights_tail = lightsValue;
          sanitized.lights_turn = lightsValue;
        }
      }
    });

    if (input.has_trailer) {
      const trailerItems = [
        "trailer_brake_connections",
        "trailer_brakes",
        "trailer_coupling_devices",
        "trailer_coupling_king_pin",
        "trailer_doors",
        "trailer_hitch",
        "trailer_landing_gear",
        "trailer_lights_all",
        "trailer_reflectors_and_tape",
        "trailer_roof",
        "trailer_suspension_system",
        "trailer_tarpaulin",
        "trailer_tires",
        "trailer_wheels_and_rims",
      ];

      trailerItems.forEach((item) => {
        if (input[item]) {
          if (input[item] === "true") {
            sanitized[item] = "~";
          } else if (input[item] === "false") {
            sanitized[item] = "X";
            failedItems.push(item.replace(/_/g, " ").toUpperCase());
          } else if (input[item] === "not_applicable") {
            sanitized[item] = "N/A";
          }
        }
      });
    }

    // Add other inspection details
    sanitized.creation_date = input.creation_date?.split("T")[0] || "";
    sanitized.time = input.time
      ? `${input.time} ${new Date(`2000-01-01T${input.time}`).getHours() >= 12 ? "PM" : "AM"}`
      : "";
    sanitized.trip_type = input.trip_type || "";
    sanitized.employee_id = input.employee_id || "";
    sanitized.address = input.address || "";
    sanitized.odometer = input.odometer?.toString() || "";
    sanitized.hours = input.hours?.toString() || "";
    sanitized.sparks_vehicle_id = input.sparks_vehicle_id || "";
    sanitized.sparks_trailer_id = input.has_trailer
      ? input.sparks_trailer_id || ""
      : "No Trailer Attached";
    sanitized.remarks = input.remarks || "";
    sanitized.repair_comments = input.repair_comments || "";
    sanitized.repair_invoice_reference = input.repair_invoice_reference || "";
    sanitized.repair_date = input.repair_date_completed?.split("T")[0] || "";
    sanitized.repair_time = input.repair_time_completed || "";
    sanitized.repair_employee_id = input.repair_employee_id || "";

    // Add inspection result to remarks
    if (failedItems.length > 0) {
      sanitized.report_overview = `Failed Items: ${failedItems.join(", ")}\n${input.report_overview || ""}`;
    } else {
      sanitized.report_overview = `All Items PASSED Inspection\n${input.report_overview || ""}`;
    }

    const templatePromise = input.is_repaired
      ? pdfTemplatePromiseDVInspectWithRepairs()
      : pdfTemplatePromiseDVInspect();

    const pdf = await generate({
      template: JSON.parse(JSON.stringify(await templatePromise)),
      inputs: [sanitized],
      plugins: {
        text,
        image,
      },
    });

    return pdf;
  } catch (error) {
    console.error("Error generating DV Inspect PDF:", error);
    throw error;
  }
};

export function generatePassword(): string {
  const charset =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let password = "";
  for (let i = 0; i < 8; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    password += charset[randomIndex];
  }
  return password;
}

export function formatDateForPostgres(
  dateString: string | undefined
): string | null {
  if (!dateString) {
    return null;
  }

  const dateWithoutTime = dateString.split(" ")[0];

  const dateParts = dateWithoutTime.split("/");

  if (dateParts.length !== 3) {
    console.error("Date string is in an unexpected format:", dateString);
    return null;
  }

  const [month, day, year] = dateParts;

  if (
    isNaN(Number(month)) ||
    isNaN(Number(day)) ||
    isNaN(Number(year)) ||
    Number(month) < 1 ||
    Number(month) > 12 ||
    Number(day) < 1 ||
    Number(day) > 31 ||
    Number(year) < 1000 ||
    Number(year) > 9999
  ) {
    console.error("Invalid date parts:", { month, day, year });
    return null;
  }

  if (
    (Number(month) === 2 && Number(day) > 29) ||
    ([4, 6, 9, 11].includes(Number(month)) && Number(day) > 30)
  ) {
    console.error("Invalid day for the given month:", { month, day });
    return null;
  }

  return `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}
